import { Box, Typography } from "@mui/material";
import {
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { FUNDING_TYPE_ORDER } from "../utils/fundingStages";

// Extract year from YYYY-MM-DD
const getYear = (dateStr) => {
  if (!dateStr) return null;
  const y = parseInt(dateStr.substring(0, 4), 10);
  return Number.isNaN(y) ? null : y;
};

const sortFundingTypeEntries = (entries) =>
  entries.sort(([a], [b]) => {
    const aIndex = FUNDING_TYPE_ORDER.indexOf(a);
    const bIndex = FUNDING_TYPE_ORDER.indexOf(b);

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    return a.localeCompare(b);
  });

const sortEntriesAlphabetically = (entries) =>
  [...entries].sort(([a], [b]) => a.localeCompare(b));

const MONTH_ORDER = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const filterCoverageDefinitions = [
  {
    label: "Industry",
    hasValue: (company) => Array.isArray(company.BucketIndustries) && company.BucketIndustries.length > 0,
  },
  {
    label: "Country",
    hasValue: (company) =>
      typeof company["Headquarters Location"] === "string" &&
      company["Headquarters Location"].trim() !== "" &&
      company["Headquarters Location"] !== "Unknown",
  },
  {
    label: "Investment Stage",
    hasValue: (company) =>
      typeof company["Investment Stage"] === "string" &&
      company["Investment Stage"].trim() !== "" &&
      company["Investment Stage"] !== "Unknown",
  },
  {
    label: "New Company",
    hasValue: (company) =>
      (company["Update Categories"] || []).includes("New Company"),
  },
  {
    label: "Updates",
    hasValue: (company) =>
      (company["Update Categories"] || []).includes("Updates"),
  },
  {
    label: "Reclassified",
    hasValue: (company) =>
      (company["Update Categories"] || []).includes("Reclassified"),
  },
  {
    label: "Revenue Range",
    hasValue: (company) =>
      typeof company["Estimated Revenue Range"] === "string" &&
      company["Estimated Revenue Range"].trim() !== "",
  },
  {
    label: "Time Period",
    hasValue: (company) =>
      typeof company["Funding Quarter"] === "string" && company["Funding Quarter"].trim() !== "",
  },
  {
    label: "Year",
    hasValue: (company) =>
      typeof company["Funding Year"] === "string" && company["Funding Year"].trim() !== "",
  },
];

const getActiveChartEntry = (state) =>
  state?.activePayload?.[0]?.payload || state?.payload || state || null;

export default function Overview({
  companies,
  fundingQuarterCompanies = companies,
  selectedFundingPeriod = [],
  onClassificationSelect,
  onFilterSelect,
}) {
  const totalCompanies = companies.length;
  const totalLeads = companies.filter((c) => c["Lead Type"] === "ACCEPT").length;
  const totalDiscard = companies.filter((c) => c["Lead Type"] === "NOT ACCEPT").length;
  const totalUnsure = companies.filter((c) => c["Lead Type"] === "FUZZY").length;

  const classificationData = [
    { name: "Leads", value: totalLeads },
    { name: "Discard", value: totalDiscard },
    { name: "Unsure", value: totalUnsure },
  ];
  const CLASS_COLORS = ["#4caf50", "#f44336", "#fb8c00"];
  const handleClassificationClick = (entry) => {
    onClassificationSelect?.(entry?.name);
  };
  const handleIndustryClick = (state) => {
    const entry = getActiveChartEntry(state);
    onFilterSelect?.("industry", entry?.industry);
  };
  const handleStageClick = (state) => {
    const entry = getActiveChartEntry(state);
    onFilterSelect?.("stage", entry?.stage);
  };
  const handleCountryClick = (state) => {
    const entry = getActiveChartEntry(state);
    onFilterSelect?.("country", entry?.country);
  };
  const handleTimeClick = (state) => {
    const entry = getActiveChartEntry(state);
    onFilterSelect?.("fundingPeriod", entry?.[timeDistributionKey]);
  };
  const handleAgeClick = (state) => {
    const entry = getActiveChartEntry(state);
    onFilterSelect?.("companyAge", entry?.year);
  };

  const industryMap = {};
  companies.forEach((c) => {
    const buckets = c.BucketIndustries || [];
    buckets.forEach((b) => {
      industryMap[b] = (industryMap[b] || 0) + 1;
    });
  });

  const industryData = sortEntriesAlphabetically(Object.entries(industryMap)).map(
    ([industry, count]) => ({ industry, count })
  );

  const fundingStageMap = {};
  companies.forEach((c) => {
    const stage = c["Investment Stage"] || "Unknown";
    fundingStageMap[stage] = (fundingStageMap[stage] || 0) + 1;
  });

  const fundingStageData = sortFundingTypeEntries(Object.entries(fundingStageMap)).map(
    ([stage, count]) => ({ stage, count })
  );

  const countryMap = {};
  companies.forEach((c) => {
    const country = c["Headquarters Location"] || "Unknown";
    countryMap[country] = (countryMap[country] || 0) + 1;
  });

  const countryData = sortEntriesAlphabetically(Object.entries(countryMap)).map(
    ([country, count]) => ({ country, count })
  );

  const quarterOrder = ["Q1", "Q2", "Q3", "Q4"];
  const fundingQuarterMap = {};
  fundingQuarterCompanies.forEach((c) => {
    const quarter = c["Funding Quarter"];
    if (!quarter) return;
    fundingQuarterMap[quarter] = (fundingQuarterMap[quarter] || 0) + 1;
  });

  const fundingQuarterData = quarterOrder
    .filter((quarter) => fundingQuarterMap[quarter])
    .map((quarter) => ({
      quarter,
      count: fundingQuarterMap[quarter],
    }));

  const fundingMonthMap = {};
  companies.forEach((c) => {
    const month = c["Funding Month"];
    if (!month) return;
    fundingMonthMap[month] = (fundingMonthMap[month] || 0) + 1;
  });

  const fundingMonthData = MONTH_ORDER
    .filter((month) => fundingMonthMap[month])
    .map((month) => ({
      month,
      count: fundingMonthMap[month],
    }));

  const isMonthPeriodSelected =
    selectedFundingPeriod.length > 0 &&
    selectedFundingPeriod.some((period) => MONTH_ORDER.includes(period));

  const timeDistributionData = isMonthPeriodSelected
    ? fundingMonthData
    : fundingQuarterData;

  const timeDistributionKey = isMonthPeriodSelected ? "month" : "quarter";

  const currentYear = new Date().getFullYear();
  const ages = companies
    .map((c) => {
      const year = getYear(c.Founded);
      return year ? currentYear - year : null;
    })
    .filter((a) => a !== null);

  const ageBins = [
    { label: "0-3 yrs", min: 0, max: 3 },
    { label: "3-6 yrs", min: 3, max: 6 },
    { label: "6-10 yrs", min: 6, max: 10 },
    { label: "10+ yrs", min: 10, max: 500 },
  ];

  const ageData = ageBins.map((bin) => ({
    year: bin.label,
    count: ages.filter((a) => a >= bin.min && a < bin.max).length,
  }));

  const filterCoverageData = filterCoverageDefinitions
    .map(({ label, hasValue }) => ({
      field: label,
      count: companies.filter((company) => hasValue(company)).length,
    }));

  return (
    <Box sx={{ mt: 0.5 }}>
      <Box
        sx={{
          px: 2,
          pt: 0,
          pb: 2,
          mb: 1,
          borderRadius: 3,
        }}
      >
        <Typography variant="h6" sx={{ mt: 0, mb: 1 }}>
          Classification Summary
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "220px minmax(0, 1fr)" },
            alignItems: "start",
            gap: 2,
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: "grid",
              justifyItems: "start",
              gap: 0.35,
            }}
          >
            <Typography variant="body1">
              <strong>Total Companies:</strong> {totalCompanies}
            </Typography>
            <Typography variant="body1">
              <strong>Leads:</strong> {totalLeads}
            </Typography>
            <Typography variant="body1">
              <strong>Unsure:</strong> {totalUnsure}
            </Typography>
            <Typography variant="body1">
              <strong>Discard:</strong> {totalDiscard}
            </Typography>
          </Box>

          <Box sx={{ width: "100%", mt: { xs: 1, md: 0.25 }, ml: { md: -3.5 } }}>
            <ResponsiveContainer width="100%" height={248}>
                <PieChart>
                  <Pie
                    data={classificationData}
                    dataKey="value"
                    nameKey="name"
                    cy="44%"
                    outerRadius={98}
                    labelLine={false}
                    onClick={handleClassificationClick}
                    cursor={onClassificationSelect ? "pointer" : "default"}
                  >
                  {classificationData.map((entry, i) => (
                    <Cell key={i} fill={CLASS_COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" align="center" />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>

      <Typography variant="h5" sx={{ mt: 5, mb: 2 }}>
        Filter Field Coverage
      </Typography>

      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={filterCoverageData} layout="vertical" margin={{ left: 20, right: 16 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" allowDecimals={false} />
          <YAxis type="category" dataKey="field" width={110} />
          <Tooltip />
          <Legend />
          <Bar dataKey="count" fill="#14b8a6" />
        </BarChart>
      </ResponsiveContainer>

      <Typography variant="h5" sx={{ mt: 5, mb: 2 }}>
        Industry Distribution
      </Typography>

      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={industryData} onClick={handleIndustryClick}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="industry"
            angle={0}
            textAnchor="middle"
            height={50}
          />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar
            dataKey="count"
            fill="#1976d2"
            onClick={handleIndustryClick}
            cursor={onFilterSelect ? "pointer" : "default"}
            activeBar={{ stroke: "#0f172a", strokeOpacity: 0.18, strokeWidth: 10 }}
            minPointSize={8}
          />
        </BarChart>
      </ResponsiveContainer>

      <Typography variant="h5" sx={{ mt: 5, mb: 2 }}>
        Funding Type Distribution
      </Typography>

      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={fundingStageData} onClick={handleStageClick}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="stage" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar
            dataKey="count"
            fill="#9c27b0"
            onClick={handleStageClick}
            cursor={onFilterSelect ? "pointer" : "default"}
            activeBar={{ stroke: "#0f172a", strokeOpacity: 0.18, strokeWidth: 10 }}
            minPointSize={8}
          />
        </BarChart>
      </ResponsiveContainer>

      <Typography variant="h5" sx={{ mt: 5, mb: 2 }}>
        Geographic Distribution
      </Typography>

      <ResponsiveContainer width="100%" height={340}>
        <BarChart data={countryData} onClick={handleCountryClick}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="country"
            angle={-90}
            textAnchor="end"
            height={120}
          />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar
            dataKey="count"
            fill="#4caf50"
            onClick={handleCountryClick}
            cursor={onFilterSelect ? "pointer" : "default"}
            activeBar={{ stroke: "#0f172a", strokeOpacity: 0.18, strokeWidth: 10 }}
            minPointSize={8}
          />
        </BarChart>
      </ResponsiveContainer>

      <Typography variant="h5" sx={{ mt: 5, mb: 2 }}>
        Funding Distribution Over Time
      </Typography>

      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={timeDistributionData} onClick={handleTimeClick}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={timeDistributionKey} />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar
            dataKey="count"
            fill="#0f766e"
            onClick={handleTimeClick}
            cursor={onFilterSelect ? "pointer" : "default"}
            activeBar={{ stroke: "#0f172a", strokeOpacity: 0.18, strokeWidth: 10 }}
            minPointSize={8}
          />
        </BarChart>
      </ResponsiveContainer>

      <Typography variant="h5" sx={{ mt: 5, mb: 2 }}>
        Company Age Distribution
      </Typography>

      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={ageData} onClick={handleAgeClick}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar
            dataKey="count"
            fill="#3f51b5"
            onClick={handleAgeClick}
            cursor={onFilterSelect ? "pointer" : "default"}
            activeBar={{ stroke: "#0f172a", strokeOpacity: 0.18, strokeWidth: 10 }}
            minPointSize={8}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
